import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius as radius, borderWidth } from "../../../design-system/tokens/borders";
import {
  KG_DATA_MODEL_NODE_WIDTH_PX,
  KG_DATA_MODEL_NODE_HEIGHT_PX,
  KG_DATA_MODEL_TEXT_MAX_WIDTH_PX,
  KG_DATA_MODEL_ICON_TOP_PX,
  KG_NODE_LABEL_TEXT_GAP_PX,
} from "../../kg/kgNodeDimensions";
import {
  kgNodeEllipsisTextCenterStyles,
  kgNodeTextContainerStyles,
} from "../../kg/kgNodeText.styles";
import type { KgNodeVisualState } from "../../kg/kgNodeShared.types";

/** Figma 2749:280836 — no drop shadow on the card wrapper (shadow was rectangular). */
export const Root = styled.div<{
  $isSelected: boolean;
  $visualState: KgNodeVisualState;
}>`
  position: relative;
  width: ${KG_DATA_MODEL_NODE_WIDTH_PX}px;
  height: ${KG_DATA_MODEL_NODE_HEIGHT_PX}px;
  flex-shrink: 0;
  cursor: pointer;
  outline: none;
  box-shadow: ${({ $isSelected }) =>
    $isSelected ? `0 0 0 8px ${rawColors.accent.dataModelRedBg}` : "none"};
  transition: box-shadow 0.25s ease;
`;

export const CubeShape = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  overflow: visible;
`;

/** Content overlay — upright text/icon on top of the cube faces. */
export const Content = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${KG_DATA_MODEL_ICON_TOP_PX}px;
  padding-bottom: ${spacing[3]};
  pointer-events: none;
`;

export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${radius.md};
  background: ${rawColors.accent.dataModelRedBg};
  color: ${rawColors.accent.dataModelRed};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${KG_NODE_LABEL_TEXT_GAP_PX}px;
  margin-top: auto;
  max-width: ${KG_DATA_MODEL_TEXT_MAX_WIDTH_PX}px;
  ${kgNodeTextContainerStyles}
`;

export const Label = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
  ${kgNodeEllipsisTextCenterStyles}
`;

export const Name = styled.span`
  ${typographyMixin("caption", 1)}
  color: ${rawColors.text.highEmphasis};
  ${kgNodeEllipsisTextCenterStyles}
`;

export const cubeStrokeProps = (visualState: KgNodeVisualState) => ({
  stroke: rawColors.accent.dataModelRed,
  strokeWidth: borderWidth[1],
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  strokeDasharray: visualState === "draft" ? "4 3" : undefined,
});
